import type { FeatureCollection } from 'geojson';
import type { GISFormat } from '../types/gisFormat';
import { UnsupportedFormatError } from '../types/gisFormat';
import { detectFormat } from './formatDetection';
import { toGeoJSON as kmlToGeoJSON } from './converters/kmlConverter';
import { toGeoJSON as gpxToGeoJSON } from './converters/gpxConverter';
import { toGeoJSON as shapefileToGeoJSON } from './converters/shapefileConverter';
import { toGeoJSON as geopackageToGeoJSON } from './converters/geopackageConverter';
import { toGeoJSON as geojsonToGeoJSON } from './converters/geojsonConverter';
import {
  validateImport,
  filterNullGeometries,
  type ImportValidationResult,
  type ImportValidationOptions,
} from './importValidator';

/**
 * Result of a file import operation.
 */
export interface ImportResult {
  /** The validated GeoJSON FeatureCollection */
  featureCollection: FeatureCollection;
  /** The detected file format */
  format: GISFormat;
  /** Validation result with details about the imported data */
  validation: ImportValidationResult;
  /** Number of features that were filtered out (null geometries) */
  filteredCount: number;
}

/**
 * Options for the import operation.
 */
export interface ImportOptions {
  /** Whether to filter out features with null geometries (default: true) */
  filterNullGeometries?: boolean;
  /** Validation options */
  validation?: ImportValidationOptions;
}

/**
 * Error thrown when an import operation fails.
 */
export class ImportError extends Error {
  constructor(
    message: string,
    public readonly format?: GISFormat,
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'ImportError';
  }
}

/**
 * Imports a GIS file by detecting its format, converting to GeoJSON,
 * and validating the output.
 *
 * Pipeline:
 * 1. Detect format using formatDetection service
 * 2. Call the appropriate converter based on detected format
 * 3. Validate the output FeatureCollection
 * 4. Optionally filter null geometries
 * 5. Return the validated FeatureCollection with metadata
 *
 * @param file - The File object to import
 * @param options - Import options
 * @returns Promise resolving to ImportResult
 * @throws ImportError if the file cannot be imported
 * @throws UnsupportedFormatError if the format cannot be detected
 *
 * Validates: Requirements 9.1, 9.2, 9.3, 9.5
 */
export async function importFile(file: File, options: ImportOptions = {}): Promise<ImportResult> {
  const { filterNullGeometries: shouldFilter = true, validation: validationOptions } = options;

  // Step 1: Detect format
  const detectionResult = await detectFormat(file);
  const format = detectionResult.format;

  // Step 2: Convert to GeoJSON using the appropriate converter
  let featureCollection: FeatureCollection;
  try {
    featureCollection = await convertFile(file, format);
  } catch (error) {
    if (error instanceof ImportError || error instanceof UnsupportedFormatError) {
      throw error;
    }
    const message = error instanceof Error ? error.message : String(error);
    throw new ImportError(
      `Failed to import ${format} file "${file.name}": ${message}`,
      format,
      error
    );
  }

  // Step 3: Validate the output
  const validationResult = validateImport(featureCollection, validationOptions);

  // Step 4: Filter null geometries if requested
  let filteredCount = 0;
  if (shouldFilter && validationResult.nullGeometryCount > 0) {
    filteredCount = validationResult.nullGeometryCount;
    featureCollection = filterNullGeometries(featureCollection);
  }

  return {
    featureCollection,
    format,
    validation: validationResult,
    filteredCount,
  };
}

/**
 * Converts a file to GeoJSON using the appropriate converter for the given format.
 *
 * @param file - The File object to convert
 * @param format - The detected GIS format
 * @returns Promise resolving to a GeoJSON FeatureCollection
 */
async function convertFile(file: File, format: GISFormat): Promise<FeatureCollection> {
  switch (format) {
    case 'geojson': {
      const text = await file.text();
      return geojsonToGeoJSON(text);
    }
    case 'kml': {
      const buffer = await file.arrayBuffer();
      return kmlToGeoJSON(buffer);
    }
    case 'gpx': {
      const text = await file.text();
      return gpxToGeoJSON(text);
    }
    case 'shapefile': {
      const buffer = await file.arrayBuffer();
      return shapefileToGeoJSON(buffer);
    }
    case 'geopackage': {
      const buffer = await file.arrayBuffer();
      return geopackageToGeoJSON(buffer);
    }
    default:
      throw new ImportError(`Unsupported format: ${format}`, format);
  }
}
